import { useEffect } from 'react';
import { useListContext } from 'react-admin';
import { sessionRole } from '../../authProvider';
import {
  OnlyMineToggle,
  onlyMineByDefault,
  onlyMineStorageKey,
  readOnlyMineChoice,
  useCaretakerPreference,
  writeOnlyMineChoice,
} from './myVehicles';

/**
 * „Tylko moje pojazdy” na liście floty — filtr `caretaker` po stronie API.
 *
 * Filtr dla wygody, nie uprawnienie (`myVehicles.tsx`). Stan pamięta
 * react-admin razem z resztą filtrów listy. Zapamiętany opiekun inny niż
 * bieżąca preferencja (zmieniona albo wyczyszczona) jest zdejmowany —
 * pusta preferencja nie może niczego ukrywać.
 *
 * Planista zaczyna od swoich aut (01.10.2026, omówienie: „Samochody widzi
 * swoje pojazdy”): bez filtra na liście i bez własnego wyboru filtr ustawia
 * się sam. Wyłączenie zapamiętuje przeglądarka, więc widok domyślny nie
 * wraca wbrew użytkownikowi. Inne role — bez zmian.
 */
export const CARETAKER_FILTER = 'caretaker';

const CHOICE_KEY = onlyMineStorageKey('cars');

export const FleetListOnlyMine = () => {
  const { filterValues, displayedFilters, setFilters } = useListContext();
  const { caretaker, loaded } = useCaretakerPreference();
  const current = filterValues?.[CARETAKER_FILTER] as string | undefined;
  const defaultOn = onlyMineByDefault(sessionRole());

  const withCaretaker = (value: string | null) => {
    const next: Record<string, unknown> = { ...(filterValues ?? {}) };
    delete next[CARETAKER_FILTER];
    if (value !== null) next[CARETAKER_FILTER] = value;
    return next;
  };

  useEffect(() => {
    if (loaded && current !== undefined && current !== caretaker) {
      setFilters(withCaretaker(null), displayedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, current, caretaker]);

  // Widok domyślny planisty — tylko gdy nie wyłączył go sam.
  useEffect(() => {
    if (!defaultOn || !loaded || caretaker === null || current !== undefined) return;
    if (readOnlyMineChoice(CHOICE_KEY) === false) return;
    setFilters(withCaretaker(caretaker), displayedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOn, loaded, caretaker, current]);

  return (
    <OnlyMineToggle
      caretaker={caretaker}
      checked={caretaker !== null && current === caretaker}
      onChange={(on) => {
        if (defaultOn) writeOnlyMineChoice(CHOICE_KEY, on, defaultOn);
        setFilters(withCaretaker(on ? caretaker : null), displayedFilters);
      }}
    />
  );
};
